import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Login as LoginIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getCustomerIdFromSubdomain, API_BASE_URL } from '../../utils/constants';
import api from '../../services/api';

// Helper function to read a cookie by name
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Helper function to clear the SSO token cookie
function clearSSOCookie() {
  // Get the root domain (e.g., ".example.com" from "customer.example.com")
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const rootDomain = parts.length > 2 ? '.' + parts.slice(-2).join('.') : '.' + hostname;

  document.cookie = `sso_token=; Domain=${rootDomain}; Path=/sso-callback; Max-Age=0`;
}

const SSOCallback = ({ onLogin }) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleTokenFromUrl = async () => {
      // Read token from cookie instead of URL params
      const token = getCookie('sso_token');

      // Clear the cookie immediately for security
      if (token) {
        clearSSOCookie();
      }

      if (!token) {
        setError('No authentication token found. Please try logging in again.');
        setLoading(false);
        return;
      }

      try {
        // Parse token parts to get expiry and user info
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          throw new Error('Invalid token format');
        }

        // Decode base64 payload
        const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(
          decodeURIComponent(
            atob(base64Payload)
              .split('')
              .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          )
        );

        // Get customer ID from subdomain
        const customerId = getCustomerIdFromSubdomain();

        // Call backend token-exchange endpoint to get full token pair (access + refresh tokens)
        // The token from URL already contains adminId and customerId, so we exchange it for a proper token pair
        console.log('Calling backend token-exchange to get token pair...');
        const tokenExchangeUrl = `${API_BASE_URL}/api/v1/customers/${customerId}/auth/token-exchange`;
        const ssoResponse = await fetch(tokenExchangeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ accessToken: token }),
        });

        if (!ssoResponse.ok) {
          const errorData = await ssoResponse.json().catch(() => ({}));
          throw new Error(errorData.error || 'SSO callback failed');
        }

        const ssoData = await ssoResponse.json();
        console.log('SSO callback response:', ssoData);

        // Extract token pair from response
        const authData = ssoData.data || ssoData;
        const accessToken = authData.accessToken || authData.token;
        const refreshToken = authData.refreshToken;
        const accessExpiresAt = authData.accessExpiresAt || authData.expiresAt;
        const refreshExpiresAt = authData.refreshExpiresAt;

        if (!accessToken) {
          throw new Error('No access token in response');
        }

        // Create admin details object for customer admin context
        const adminDetails = {
          username: authData.username || 'admin',
          firstName: '',
          isAdmin: authData.isAdmin !== undefined ? authData.isAdmin : true,
          role: authData.role || 'sso_admin',
          customerId: authData.customerId || customerId
        };

        // Store tokens using the api service
        if (refreshToken && refreshExpiresAt) {
          // Use the new token pair method
          api.setTokenPair(accessToken, accessExpiresAt, refreshToken, refreshExpiresAt);
          console.log('Stored token pair with refresh token');
        } else {
          // Fallback to single token
          api.setAuthToken(accessToken, accessExpiresAt);
          console.log('Stored single access token (no refresh token in response)');
        }

        // Fetch user details from Keycloak to get firstName
        try {
          const userResponse = await api.getUser(adminDetails.username);
          console.log('User details response:', userResponse);
          if (userResponse.success && userResponse.data) {
            adminDetails.firstName = userResponse.data.firstName || '';
          }
        } catch (err) {
          console.warn('Could not fetch user details for firstName:', err);
        }

        // Store admin details in localStorage
        console.log('Storing adminDetails:', adminDetails);
        localStorage.setItem('adminDetails', JSON.stringify(adminDetails));

        // Call onLogin to update app state
        if (onLogin) {
          onLogin({
            token: accessToken,
            expiresAt: accessExpiresAt,
            accessToken,
            refreshToken,
            accessExpiresAt,
            refreshExpiresAt,
            username: adminDetails.username,
            firstName: adminDetails.firstName,
            isAdmin: adminDetails.isAdmin,
            role: adminDetails.role,
            customerId: adminDetails.customerId
          });
        }

        // Show success state briefly before redirect
        setLoading(false);
        setSuccess(true);
        toast.success('Authentication successful!');

        // Redirect to dashboard
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      } catch (err) {
        console.error('Error processing SSO token:', err);
        setError('Failed to process authentication. Please try logging in again.');
        setLoading(false);
        toast.error('Authentication failed');
      }
    };

    handleTokenFromUrl();
  }, [location, navigate, onLogin]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F9FBFD',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E5E5',
              borderRadius: '12px',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
            }}
          >
            {/* Logo */}
            <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img
                src="https://www.cloudkeeper.com/themes/cloud_theme/images/Cloudkeeper_New.svg"
                alt="CloudKeeper"
                style={{
                  height: '40px',
                  width: 'auto',
                  objectFit: 'contain',
                  marginBottom: '8px'
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                }}
              >
                Single Sign-On
              </Typography>
            </Box>

            {/* Loading State */}
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(70, 152, 211, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                  }}
                >
                  <CircularProgress
                    size={32}
                    thickness={4}
                    sx={{ color: '#4698D3' }}
                  />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    color: '#253E66',
                    mb: 1,
                  }}
                >
                  Completing Authentication
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#666666',
                    fontFamily: 'Metropolis, sans-serif',
                    mb: 2,
                  }}
                >
                  Please wait while we verify your credentials...
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 0.5,
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        delay: i * 0.2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#4698D3',
                        }}
                      />
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            )}

            {/* Success State */}
            {success && !loading && !error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(39, 174, 96, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 32, color: '#27AE60' }} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    color: '#27AE60',
                    mb: 1,
                  }}
                >
                  Authentication Successful
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#666666',
                    fontFamily: 'Metropolis, sans-serif',
                  }}
                >
                  Redirecting to dashboard...
                </Typography>
              </motion.div>
            )}

            {/* Error State */}
            {error && !loading && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(231, 76, 60, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                  }}
                >
                  <ErrorIcon sx={{ fontSize: 32, color: '#E74C3C' }} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    color: '#E74C3C',
                    mb: 1.5,
                  }}
                >
                  Authentication Failed
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: 'rgba(231, 76, 60, 0.08)',
                    border: '1px solid rgba(231, 76, 60, 0.2)',
                    borderRadius: '8px',
                    p: 2,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666666',
                      fontFamily: 'Metropolis, sans-serif',
                      lineHeight: 1.6,
                    }}
                  >
                    {error}
                  </Typography>
                </Paper>

                <Button
                  variant="contained"
                  startIcon={<LoginIcon />}
                  onClick={() => navigate('/')}
                  fullWidth
                  sx={{
                    backgroundColor: '#4698D3',
                    borderRadius: '6px',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    py: 1.5,
                    boxShadow: '0 2px 8px rgba(70, 152, 211, 0.25)',
                    '&:hover': {
                      backgroundColor: '#3580B8',
                      boxShadow: '0 4px 12px rgba(70, 152, 211, 0.35)',
                    },
                  }}
                >
                  Return to Login
                </Button>
              </motion.div>
            )}

            {/* Footer */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #E5E5E5' }}>
              <Typography
                variant="caption"
                sx={{
                  color: '#BDBDBD',
                  fontFamily: 'Metropolis, sans-serif',
                }}
              >
                Secure authentication powered by CK Prism
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default SSOCallback;
